import ToolbarButtonPopover from "../toolbar/ToolbarButtonPopover";
import { useState, useEffect, useContext, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useFireHistory, useUpdateFireHistory } from "../../api/fireHistory";
import * as geojson from "geojson";
import FireHistoryLayerPopover from "../FireHistoryLayerPopover";
import { StrategyContext } from "../../contexts/StrategyContext";
import { Filter } from "../layerControl/FilterModal";
import { applyFilters } from "../../utils/layerFilters";
import MapFeaturePickerMenu, { MapFeaturePickerOption } from "../MapFeaturePickerMenu";
import { useUnitSystem } from "../../hooks/useUnitSystem";
import { useTimezone } from "../../contexts/TimezoneContext";
import { formatArea } from "../../utils/units";
import { formatInTimezone } from "../../utils/datetime";
import { mapEventScreenPoint } from "../../utils/uiZoom";

const UNKNOWN_YEAR = "unknown";
const UNKNOWN_COLOR = "#808080";

const getColorForDate = (endDate: Date, minYear: number) => {
  const date = new Date(endDate);
  const year = date.getFullYear();
  const range = 2030 - minYear
  const hue = 120 * (year - minYear)/range
  return `hsl(${hue % 360}, 100%, 50%)`; // Use HSL for consistent saturation and lightness
};

// Colours, year buckets and labels, derived from the fire features as the
// API returns them. Dated fires sit on a hue ramp from the oldest fire;
// undated ones are grey in the "unknown" bucket. Rebuilt whenever a fire's
// date changes, because the ramp and the buckets both depend on every fire.
const buildLayerData = (rawFeatures: geojson.Feature[]) => {
  const today = new Date().toISOString().split('T')[0];

  const datedFeatures = rawFeatures
    .filter((f) => !f?.properties?.DateMissing && f?.properties?.EndDate > "1910-01-01" && f?.properties?.EndDate < today)
    .sort((a, b) => new Date(a?.properties?.EndDate).getTime() - new Date(b?.properties?.EndDate).getTime());

  const unknownFeatures = rawFeatures.filter((f) => f?.properties?.DateMissing);

  const years = [...new Set(datedFeatures.map(
    (f) => new Date(f?.properties?.EndDate).getFullYear().toString()
  ))].sort() as string[];

  if (unknownFeatures.length > 0) {
    years.push(UNKNOWN_YEAR);
  }

  const minYear = datedFeatures.length > 0
    ? Math.min(...datedFeatures.map((f) => new Date(f?.properties?.EndDate).getFullYear()))
    : new Date().getFullYear();

  const datedMapped = datedFeatures.map((feature) => {
    const color = getColorForDate(feature?.properties?.EndDate, minYear);
    return {
      ...feature,
      properties: {
        ...feature?.properties,
        color,
        hoverLabel: feature?.properties?.FireName + " " + feature?.properties?.EndDate,
        year: new Date(feature?.properties?.EndDate).getFullYear().toString(),
      },
    };
  });

  const unknownMapped = unknownFeatures.map((feature) => ({
    ...feature,
    properties: {
      ...feature?.properties,
      color: UNKNOWN_COLOR,
      hoverLabel: (feature?.properties?.FireName ?? 'Historical Fire') + ' (date unknown)',
      year: UNKNOWN_YEAR,
    },
  }));

  return {
    years,
    collection: {
      type: "FeatureCollection",
      features: [...datedMapped, ...unknownMapped],
    },
  };
};

const FireHistoryLayer = (
  id: string,
  name: string,
  filters: Filter[] = []) => {
  const { projectId } = useParams<{ projectId: string }>();
  const { history } = useFireHistory(projectId ?? "");
  const updateFireHistory = useUpdateFireHistory(projectId ?? "");
  const { refreshStrategy } = useContext(StrategyContext);
  const [geojson, setGeojson] = useState<any>(null); // Initialize geojson as null
  const [filteredGeojson, setFilteredGeojson] = useState<any>(null);
  const [active, setActive] = useState(false);
  const [stale, setStale] = useState(false);
  const [selectedYears, setSelectedYears] = useState<string[]>(["none"]);
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [availableYears, setAvailableYears] = useState<string[]>([]);
  const [selectedFeature, setSelectedFeature] = useState<geojson.Feature | null>(null);
  const [popoverPosition, setPopoverPosition] = useState<{ top: number; left: number } | null>(null);
  const [pendingEndDate, setPendingEndDate] = useState<string | null | undefined>(undefined);
  const [isSavingDate, setIsSavingDate] = useState(false);
  const unitSystem = useUnitSystem();
  const { selectedTimezone } = useTimezone();
  // Fires stacked under one click: the user picks which one to open.
  const [pickerFeatures, setPickerFeatures] = useState<geojson.Feature[]>([]);
  const [pickerPosition, setPickerPosition] = useState<{ top: number; left: number } | null>(null);



  const applyLayerData = useCallback((rawFeatures: geojson.Feature[]) => {
    const { years, collection } = buildLayerData(rawFeatures);
    setAvailableYears(years);
    // Drop year selections that no longer exist (the last undated fire was
    // just dated, say) so the filter does not silently show nothing.
    setSelectedYears((prev) => {
      const kept = prev.filter((y) => y === "none" || y === "all" || years.includes(y));
      if (kept.length === prev.length) return prev;
      return kept.length > 0 ? kept : ["all"];
    });
    setGeojson(collection);
    setStale(true);
    return collection;
  }, []);

  useEffect(() => {
    if (history && history.data) {
      const collection = applyLayerData(history.data.features);
      setFilteredGeojson(collection);
    }
  }, [history, applyLayerData]);

  // Filter geojson when selectedYears, visibility, or filters change
  useEffect(() => {
    if (geojson) {
      // Check if "none" is selected
      if (selectedYears.includes("none")) {
        // If "None" is selected, hide all features
        const emptyFeatures = {
          ...geojson,
          features: []
        };
        setFilteredGeojson(emptyFeatures);
        setActive(false);
      }
      // Check if "all" is selected
      else if (selectedYears.includes("all")) {
        const userFiltered = applyFilters(geojson, filters);
        setFilteredGeojson(userFiltered);
        setActive(isVisible);
      }
      // Otherwise filter by selected years
      else {
        const yearFiltered = {
          ...geojson,
          features: geojson.features.filter(
            (feature: geojson.Feature) => selectedYears.includes(feature.properties?.year || "")
          )
        };
        const userFiltered = applyFilters(yearFiltered, filters);
        setFilteredGeojson(userFiltered);
        setActive(isVisible && userFiltered.features.length > 0);
      }
      setStale(true);
    }
  }, [selectedYears, isVisible, geojson, filters]);

  const layerStyle = {
    id: id,
    type: "fill",
    paint: {
      "fill-color": ['get', 'color'],
      "fill-opacity": 0.75,
    },
    layout: {
      visibility: active ? 'visible' : 'none',
    },
  };

  // Add a second layer for hover state
  const hoverLayerStyle = {
    id: `${id}-hover`,
    type: "fill",
    paint: {
      "fill-color": ['get', 'color'],
      "fill-opacity": 1.0,
      "fill-outline-color": "#ffffff",
    },
    layout: {
      visibility: active ? 'visible' : 'none',
    },
    filter: ["==", "__id", ""],
  };

  const handleYearSelect = (year: string) => {
    // Special case for "none" - always clear other selections
    if (year === "none") {
      setIsVisible(false);
      setSelectedYears(["none"]);
      return;
    }

    // Get current selected years excluding none and all
    const currentSelected = selectedYears.filter(y => y !== "none" && y !== "all");

    // Special case for "all" - replace all individual year selections
    if (year === "all") {
      setIsVisible(true);
      setSelectedYears(["all"]);
      return;
    }

    // Handle toggling of individual years
    setIsVisible(true);
    if (currentSelected.includes(year)) {
      // If this year is already selected, remove it
      const newSelectedYears = currentSelected.filter(y => y !== year);
      // If no years left, default to "all"
      if (newSelectedYears.length === 0) {
        setSelectedYears(["all"]);
      } else {
        setSelectedYears(newSelectedYears);
      }
    } else {
      // If selecting a specific year, remove "all" if it's currently selected
      const newSelectedYears = selectedYears.includes("all") ? [] : currentSelected;
      setSelectedYears([...newSelectedYears, year]);
    }
  };

  const getPopoverOptions = () => {
    const options = [
      {
        id: "none",
        label: "None",
        onClick: () => handleYearSelect("none"),
        selected: selectedYears.includes("none"),
        divider: true
      },
      {
        id: "all",
        label: "All Years",
        onClick: () => handleYearSelect("all"),
        selected: selectedYears.includes("all"),
        divider: false
      },
    ];

    availableYears.forEach(year => {
      options.push({
        id: year,
        label: year === "unknown" ? "Unknown Date" : `${year}`,
        onClick: () => handleYearSelect(year),
        selected: selectedYears.includes(year),
        divider: false
      });
    });

    return options;
  };

  const handleToggle = () => {
    if (isVisible) {
      // Turning off
      setIsVisible(false);
      setSelectedYears(["none"]);
    } else {
      // Turning on
      setIsVisible(true);
      setSelectedYears(["all"]);
    }
  };

  const toolbarButton = (
      <ToolbarButtonPopover
        key={id}
        id={id}
        name={"Fire History"}
        enabled={!!geojson}
        toggledOn={active}
        icon={"local_fire_department"}
        options={getPopoverOptions()}
        onClick={handleToggle}
        stayOpenAfterSelection={true}
      />
  )

  const featureKey = (feature: geojson.Feature) =>
    feature.id ?? feature.properties?.__id ?? feature.properties?.id;

  // Popovers anchor to the click's viewport position (what MUI's
  // anchorPosition expects); e.point is relative to the map canvas.
  const anchorFromEvent = mapEventScreenPoint;

  const openFeature = (feature: geojson.Feature, position: { top: number; left: number }) => {
    setSelectedFeature(feature);
    setPopoverPosition(position);
    setPendingEndDate(undefined);
    setIsSavingDate(false);
  };

  const closePicker = () => {
    setPickerFeatures([]);
    setPickerPosition(null);
  };

  const handleFeatureClick = (e: any) => {
    // Only handle clicks if the layer is active
    if (!active) return;

    const features: geojson.Feature[] = e.features ?? [];
    if (features.length === 0) return;

    // A polygon that spans a tile boundary comes back once per tile, so
    // collapse to one entry per fire before deciding whether to ask.
    const seen = new Set<string | number>();
    const unique = features.filter((feature) => {
      const key = featureKey(feature);
      if (key === undefined || key === null) return true;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    const position = anchorFromEvent(e);
    if (unique.length > 1) {
      // Several fires overlap here: ask which one, most recent first and
      // undated fires last.
      const sorted = [...unique].sort((a, b) => {
        const aMissing = Boolean(a.properties?.DateMissing);
        const bMissing = Boolean(b.properties?.DateMissing);
        if (aMissing !== bMissing) return aMissing ? 1 : -1;
        return String(b.properties?.EndDate ?? "").localeCompare(String(a.properties?.EndDate ?? ""));
      });
      setSelectedFeature(null);
      setPopoverPosition(null);
      setPickerFeatures(sorted);
      setPickerPosition(position);
      return;
    }

    openFeature(unique[0], position);
  };

  const pickerOptions: MapFeaturePickerOption[] = pickerFeatures.map((feature, index) => {
    const props = feature.properties ?? {};
    const when = props.DateMissing
      ? "Date unknown"
      : formatInTimezone(props.EndDate ? String(props.EndDate) : null, selectedTimezone, "DD/MM/YYYY");
    const areaHa = Number(props.AreaHa);
    const detail = Number.isFinite(areaHa) && areaHa > 0
      ? `${when} · ${formatArea(areaHa, unitSystem)}`
      : when;
    return {
      key: featureKey(feature) ?? index,
      primary: props.FireName || "Historical Fire",
      secondary: detail,
      color: props.color,
    };
  });

  const handleClosePopover = () => {
    setSelectedFeature(null);
    setPopoverPosition(null);
    setPendingEndDate(undefined);
    setIsSavingDate(false);
  };

  const syncSelectedFeatureFromSource = (featureId: geojson.Feature['id']) => {
    if (!geojson) return;
    const latest = geojson.features.find((feature: geojson.Feature) => feature.id === featureId);
    if (latest) {
      setSelectedFeature(latest);
    }
  };

  const handlePendingDateChange = (
    field: 'StartDate' | 'EndDate',
    value: string | null
  ) => {
    if (!selectedFeature?.id || field !== 'EndDate') {
      return;
    }

    setPendingEndDate(value);
    setSelectedFeature((prev) =>
      prev
        ? {
            ...prev,
            properties: {
              ...prev.properties,
              EndDate: value ?? prev.properties?.EndDate,
            },
          }
        : prev
    );
  };

  const handleCancelPendingDate = () => {
    if (!selectedFeature?.id) {
      setPendingEndDate(undefined);
      return;
    }
    syncSelectedFeatureFromSource(selectedFeature.id);
    setPendingEndDate(undefined);
  };

  const handleSavePendingDate = () => {
    if (!selectedFeature?.id || pendingEndDate === undefined || !projectId) {
      return;
    }

    const previousGeojson = geojson;
    const previousFilteredGeojson = filteredGeojson;
    const featureId = selectedFeature.id;

    setIsSavingDate(true);
    updateFireHistory.mutate(
      {
        fireHistoryId: featureId as string | number,
        updates: { endDate: pendingEndDate ?? null },
      },
      {
        onSuccess: (response) => {
          // Rebuild the layer from the server's copy of the fire so its
          // colour, year bucket and date-missing flag follow the new date,
          // not just the date text. Without this a fire that had no date
          // stayed grey and "Date unknown" after being dated.
          const saved = response?.data as geojson.Feature | undefined;
          const rawFeatures = (previousGeojson?.features ?? []).map((feature: geojson.Feature) => {
            if (feature.id !== featureId) return feature;
            if (saved?.properties) {
              return { ...feature, properties: { ...feature.properties, ...saved.properties } };
            }
            return {
              ...feature,
              properties: {
                ...feature.properties,
                EndDate: pendingEndDate,
                StartDate: feature.properties?.DateMissing ? pendingEndDate : feature.properties?.StartDate,
                DateMissing: false,
              },
            };
          });
          const rebuilt = applyLayerData(rawFeatures);
          const latest = rebuilt.features.find((feature: geojson.Feature) => feature.id === featureId);
          if (latest) setSelectedFeature(latest);
          setPendingEndDate(undefined);
          refreshStrategy();
        },
        onError: () => {
          setGeojson(previousGeojson);
          setFilteredGeojson(previousFilteredGeojson);
          syncSelectedFeatureFromSource(featureId);
        },
        onSettled: () => {
          setIsSavingDate(false);
        },
      }
    );
  };

  const interactiveLayerIds = active ? [id] : [];

  return {
    id: id,
    name: name,
    source: filteredGeojson,
    rawSource: geojson,
    layerStyle: [layerStyle, hoverLayerStyle],
    toolbarButton: toolbarButton,
    active: active,
    stale: stale,
    setStale: setStale,
    onClick: handleFeatureClick,
    interactiveLayerIds: interactiveLayerIds,
    popover: (
      <>
        <MapFeaturePickerMenu
          anchorPosition={pickerPosition}
          options={pickerOptions}
          onSelect={(index) => {
            const feature = pickerFeatures[index];
            const position = pickerPosition;
            closePicker();
            if (feature && position) openFeature(feature, position);
          }}
          onClose={closePicker}
        />
        <FireHistoryLayerPopover
          feature={selectedFeature}
          anchorPosition={popoverPosition}
          onClose={handleClosePopover}
          onUpdateDate={handlePendingDateChange}
          onSaveChanges={handleSavePendingDate}
          onCancelPending={handleCancelPendingDate}
          hasPendingChanges={pendingEndDate !== undefined}
          isSaving={isSavingDate}
          draftEndDate={pendingEndDate}
        />
      </>
    )
  };
};

export default FireHistoryLayer;
